import { useRef } from 'react';
import { Link } from 'react-router-dom';
import useGsapHero from '../../hooks/useGsapHero';
import useGsapSection from '../../hooks/useGsapSection';
import useGsapInView from '../../hooks/useGsapInView';
import { BlurSplitText, ScrambleReveal, ClipRevealText } from '../../components/TextEffects';
import ContactTrigger from '../../components/ContactTrigger';
import { ABOUT_CLIENTS, ABOUT_CERTIFICATIONS, ABOUT_DISCIPLINES, ABOUT_FACTS } from '../../data/about';
import './AboutPage.css';

const APPROACH_COPY = [
  'Most projects fail between the brief and the execution. Someone translates the idea wrong, or builds the right thing in the wrong direction.',
  'I work across both sides. I read the brief as a designer and execute it as a developer. No translation loss. No back and forth between two people who speak different languages.',
  'The result is work that looks intentional, because it is.',
];

const BIO_COPY = [
  'Independent since 2019, collaborating with agencies, studios and brands across Europe and the Middle East. I work across graphic design, web development, 3D and AI-driven production. One person, full process.',
  'One person who understands both design and development.',
];

const AboutPage = () => {
  const headerRef = useRef(null);
  const statementRef = useRef(null);
  const approachRef = useRef(null);
  const disciplinesRef = useRef(null);
  const factsRef = useRef(null);
  const certificationsRef = useRef(null);
  const clientsRef = useRef(null);
  const ctaRef = useRef(null);

  const headerInView = useGsapHero(headerRef);
  const statementInView = useGsapInView(statementRef);
  const approachInView = useGsapSection(approachRef);
  const disciplinesInView = useGsapSection(disciplinesRef, {
    revealSelector: '.about-page-discipline',
  });
  const factsInView = useGsapSection(factsRef);
  const certificationsInView = useGsapSection(certificationsRef, {
    revealSelector: '.about-page-certification',
  });
  const clientsInView = useGsapSection(clientsRef);
  useGsapSection(ctaRef);

  return (
    <main className="about-page" data-testid="about-page">
      <header ref={headerRef} className="about-page-header">
        <span className="about-page-strip gsap-reveal">(003) —— ABOUT · ANDREA FAGIOLI</span>
        <div className="about-page-title-wrap">
          <h1 className="about-page-title">
            <ClipRevealText text="About." as="span" externalInView={headerInView} duration={0.85} />
          </h1>
          <div className="about-page-title-line gsap-hero-line" aria-hidden="true" />
        </div>
      </header>

      <section ref={statementRef} className="about-page-statement" aria-label="Opening statement">
        <h2 className="about-page-statement-text">
          <span className="about-page-statement-line">
            <BlurSplitText
              text="Design and technology,"
              as="span"
              delay={0.1}
              stagger={0.05}
              externalInView={statementInView}
            />
          </span>
          <span className="about-page-statement-line">
            <BlurSplitText
              text="in the same hands."
              as="span"
              delay={0.28}
              stagger={0.05}
              externalInView={statementInView}
            />
          </span>
        </h2>
      </section>

      <section className="about-page-bio" aria-label="Bio">
        {BIO_COPY.map((paragraph) => (
          <p key={paragraph} className="about-page-bio-p">
            {paragraph}
          </p>
        ))}
      </section>

      <section ref={approachRef} className="about-page-approach" aria-label="Approach">
        <ScrambleReveal
          text="— APPROACH"
          className="about-page-label"
          delay={0.05}
          externalInView={approachInView}
        />
        <div className="about-page-approach-copy">
          {APPROACH_COPY.map((paragraph) => (
            <p key={paragraph} className="about-page-body-p">
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      <section ref={disciplinesRef} className="about-page-disciplines" aria-label="Disciplines">
        <ScrambleReveal
          text="— DISCIPLINES"
          className="about-page-label"
          delay={0.05}
          externalInView={disciplinesInView}
        />
        <ul className="about-page-disciplines-list">
          {ABOUT_DISCIPLINES.map((item) => (
            <li key={item} className="about-page-discipline">
              <span className="about-page-discipline-dot" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section ref={factsRef} className="about-page-facts" aria-label="Facts">
        <ScrambleReveal
          text="— FACTS"
          className="about-page-label"
          delay={0.05}
          externalInView={factsInView}
        />
        <dl className="about-page-facts-grid">
          {ABOUT_FACTS.map(({ label, value, mono, href, openContact }) => (
            <div key={label} className="about-page-fact gsap-reveal">
              <dt>{label}</dt>
              <dd className={mono ? 'is-mono' : ''}>
                {openContact ? (
                  <ContactTrigger className="about-page-email">
                    {value}
                  </ContactTrigger>
                ) : href ? (
                  <a href={href} className="about-page-email">
                    {value}
                  </a>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section ref={certificationsRef} className="about-page-certifications" aria-label="Certifications">
        <ScrambleReveal
          text="— CERTIFICATIONS"
          className="about-page-label"
          delay={0.05}
          externalInView={certificationsInView}
        />
        <ul className="about-page-certifications-list">
          {ABOUT_CERTIFICATIONS.map((item) => (
            <li key={item} className="about-page-certification">
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section ref={clientsRef} className="about-page-clients" aria-label="Selected clients">
        <ScrambleReveal
          text="— Selected Clients"
          className="about-page-label"
          delay={0.05}
          externalInView={clientsInView}
        />
        <ul className="about-page-clients-grid gsap-reveal">
          {ABOUT_CLIENTS.map((name) => (
            <li key={name} className="about-page-client">
              {name}
            </li>
          ))}
        </ul>
      </section>

      <section ref={ctaRef} className="about-page-cta" aria-label="Contact">
        <p className="about-page-cta-lead gsap-reveal">
          <span className="about-page-cta-dot" aria-hidden="true" />
          Currently open for new projects.
        </p>
        <div className="about-page-cta-links gsap-reveal">
          <Link to="/work" className="about-page-cta-link" data-testid="about-cta-work">
            <span>→ View work</span>
            <span aria-hidden="true">↗</span>
          </Link>
          <ContactTrigger className="about-page-cta-link" data-testid="about-cta-contact">
            <span>→ Contact</span>
            <span aria-hidden="true">↗</span>
          </ContactTrigger>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
